import type { RunRules } from "../types/rules";
import type { FactionId, HexOwner, RunState } from "../types/state";

/**
 * Sides (K6, docs/design/KINGDOM-SEATS-IDEA.md section 9): alliances of kingdoms fixed for a run
 * by `rules.sides`; absent, every kingdom is its own side and every byte of the runs before K6
 * stays. The only module that compares two owners unequal (`test/sides.test.ts` holds it to
 * that): every other one asks which of three things it means. `hostile`, an enemy to strike, to
 * fear or to aim at; `allied`, ground or a unit on the same side, own included, which is not
 * taken, ground or walled against; `sameOwner`, the kingdom itself, which alone orders its units,
 * builds and razes on its land and counts as its own.
 */

/** The kingdoms of one side, a kingdom's own included: the ground and the units that are friends. */
export type Side = ReadonlySet<HexOwner>;

const OWN_SIDES = new Map<FactionId, Side>();
const SIDE_TABLES = new WeakMap<readonly (readonly FactionId[])[], ReadonlyMap<HexOwner, Side>>();

function sideTable(sides: readonly (readonly FactionId[])[]): ReadonlyMap<HexOwner, Side> {
  const known = SIDE_TABLES.get(sides);
  if (known) {
    return known;
  }
  const table = new Map<HexOwner, Side>();
  for (const kingdoms of sides) {
    const side: Side = new Set(kingdoms);
    for (const kingdom of kingdoms) {
      table.set(kingdom, side);
    }
  }
  SIDE_TABLES.set(sides, table);
  return table;
}

/** The side `faction` fights on: its allies and itself, or itself alone without `rules.sides`. */
export function sideOf(rules: RunRules, faction: FactionId): Side {
  const listed = rules.sides && sideTable(rules.sides).get(faction);
  if (listed) {
    return listed;
  }
  let own = OWN_SIDES.get(faction);
  if (!own) {
    own = new Set([faction]);
    OWN_SIDES.set(faction, own);
  }
  return own;
}

/** The same holder, "none" included: what a kingdom owns, orders and counts as its own. */
export function sameOwner(a: HexOwner | undefined, b: HexOwner | undefined): boolean {
  return a === b;
}

/** Two kingdoms on one side, or one kingdom with itself; nobody's ground is nobody's ally. */
export function allied(rules: RunRules, a: HexOwner | undefined, b: HexOwner | undefined): boolean {
  if (a === undefined || b === undefined || a === "none" || b === "none") {
    return false;
  }
  return a === b || (rules.sides !== undefined && sideOf(rules, a).has(b));
}

/** Two kingdoms on different sides: the only pair that strikes, takes or walls against the other. */
export function hostile(
  rules: RunRules,
  a: HexOwner | undefined,
  b: HexOwner | undefined,
): boolean {
  return a !== undefined && b !== undefined && a !== "none" && b !== "none" && !allied(rules, a, b);
}

/**
 * Every side of the run, in the order of `rules.sides`, or each kingdom alone in the run's order:
 * what the verdict compares (K6).
 */
export function sidesOfRun(state: RunState): readonly (readonly FactionId[])[] {
  return state.rules.sides ?? state.factions.map((faction) => [faction]);
}

/** The kingdoms of `faction`'s side in the run, in the side's order. */
export function kingdomsOfSide(state: RunState, faction: FactionId): readonly FactionId[] {
  return sidesOfRun(state).find((side) => side.includes(faction)) ?? [faction];
}

/**
 * Why `sides` cannot be a run's sides for `factions`, or undefined when it can: every kingdom of
 * the run on exactly one side, no side empty, and no kingdom the run does not hold.
 */
export function sidesRefusal(
  sides: readonly (readonly FactionId[])[],
  factions: readonly FactionId[],
): string | undefined {
  const listed = sides.flat();
  if (sides.some((side) => side.length === 0)) {
    return "a side holds no kingdom";
  }
  if (new Set(listed).size !== listed.length) {
    return "a kingdom is on two sides";
  }
  const missing = factions.find((faction) => !listed.includes(faction));
  if (missing !== undefined) {
    return `${missing} is on no side`;
  }
  const stranger = listed.find((faction) => !factions.includes(faction));
  return stranger === undefined ? undefined : `${stranger} is not in the run`;
}
